// /admin/ops/runs/$id — run detail. Aggregate stats, watermark
// before/after diff, per-step table, and deep links to both Airflow
// (orchestration view) and the findings inbox scoped to this run
// (business view).
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { fmtRelative } from "@/lib/format";
import { fetchRun, opsKeys } from "@/lib/ops-queries";
import type { RunStatus } from "@/lib/ops-types";

const AIRFLOW_BASE = "https://sit-sm-airflow.devcula.com";

export const Route = createFileRoute("/_app/ops/runs/$id")({
  params: {
    parse: (p) => ({ id: parseInt(p.id, 10) }),
    stringify: (p) => ({ id: String(p.id) }),
  },
  component: RunDetail,
});

function RunDetail() {
  const { id } = Route.useParams();
  const q = useQuery({
    queryKey: opsKeys.run(id),
    queryFn: () => fetchRun(id),
    // Poll while the run is in flight so operators don't need to reload.
    refetchInterval: (query) =>
      query.state.data?.Run.status === "running" ? 5_000 : false,
  });

  if (q.isLoading) return <Skeleton className="h-64" />;
  if (q.isError || !q.data)
    return <div className="text-red-500">Run not found.</div>;

  const r = q.data.Run;
  const airflowURL = `${AIRFLOW_BASE}/dags/${encodeURIComponent(r.airflow_dag_id)}/grid?dag_run_id=${encodeURIComponent(r.airflow_run_id)}`;
  const wmDiff = diffWatermark(r.watermark_before, r.watermark_after);
  const summaryEntries = entriesOf(r.summary);

  return (
    <div className="space-y-6">
      <Link to="/ops/runs" className="text-sm text-muted hover:text-fg">
        ← Back to runs
      </Link>

      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-3">
            Run #{r.run_id}
            <Badge tone={statusTone(r.status)}>{r.status}</Badge>
            <span className="font-normal text-sm text-muted">
              {r.airflow_dag_id}
            </span>
          </CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <Field label="Task type" value={r.task_type} />
            <Field label="Trigger" value={r.trigger_kind} />
            <Field label="Triggered by" value={r.triggered_by || "—"} />
            <Field label="Worker count" value={r.worker_count ?? "—"} />
            <Field
              label="Started"
              value={fmtRelative(new Date(r.started_at_unix_ms).toISOString())}
            />
            <Field
              label="Finished"
              value={
                r.finished_at_unix_ms
                  ? fmtRelative(new Date(r.finished_at_unix_ms).toISOString())
                  : "—"
              }
            />
            <Field
              label="Duration"
              value={
                r.duration_ms
                  ? `${(r.duration_ms / 1000).toFixed(1)}s`
                  : r.status === "running"
                    ? `${runningSeconds(r.started_at_unix_ms).toFixed(0)}s (in flight)`
                    : "—"
              }
            />
            <Field
              label="Records scanned"
              value={r.records_scanned.toLocaleString()}
            />
            <Field
              label="Findings new"
              value={r.findings_new}
              highlight={r.findings_new > 0}
            />
            <Field label="Findings recurring" value={r.findings_recurring} />
          </dl>

          {r.error_message && (
            <pre className="overflow-x-auto rounded border border-red-500 bg-card p-3 text-xs text-red-500">
              {r.error_message}
            </pre>
          )}

          <div className="flex flex-wrap gap-3 text-sm">
            {(r.findings_new > 0 || r.findings_recurring > 0) && (
              <Link
                to="/ops/findings"
                search={{ run_id: String(r.run_id), only_open: "true" }}
                className="underline"
              >
                View findings emitted by this run →
              </Link>
            )}
            <a
              className="ml-auto underline"
              href={airflowURL}
              target="_blank"
              rel="noreferrer"
            >
              View in Airflow ↗
            </a>
          </div>
        </CardBody>
      </Card>

      {/* Watermark diff. Side-by-side per shard — green where the watermark
          advanced, gray where it didn't. The shape is per-task-type so we
          accept any string keys + values. */}
      {wmDiff.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Watermark</CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            <table className="w-full text-sm">
              <thead className="text-2xs uppercase tracking-wide text-muted">
                <tr className="border-b border-border">
                  <th className="px-3 py-2 text-left">Shard</th>
                  <th className="px-3 py-2 text-left">Before</th>
                  <th className="px-3 py-2 text-left">After</th>
                  <th className="px-3 py-2 text-left">Δ</th>
                </tr>
              </thead>
              <tbody>
                {wmDiff.map((row) => (
                  <tr
                    key={row.key}
                    className="border-b border-border last:border-0"
                  >
                    <td className="px-3 py-2 font-mono text-xs">{row.key}</td>
                    <td className="px-3 py-2 font-mono text-xs text-muted">
                      {row.before ?? "—"}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {row.after ?? "—"}
                    </td>
                    <td className="px-3 py-2">
                      {row.advanced ? (
                        <Badge tone="success">advanced</Badge>
                      ) : (
                        <Badge tone="muted">unchanged</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}

      {/* Free-form per-task summary block (e.g. p95 latency, retry
          counts). Renders as a flat key-value table — every task type
          may pack different fields. */}
      {summaryEntries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Summary</CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            <table className="w-full text-sm">
              <tbody>
                {summaryEntries.map(([k, v]) => (
                  <tr
                    key={k}
                    className="border-b border-border last:border-0"
                  >
                    <td className="px-3 py-2 font-mono text-xs text-muted">
                      {k}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {renderScalar(v)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Steps</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {q.data.Steps.length === 0 ? (
            <div className="p-4 text-sm text-muted">
              No per-step rows yet (run_steps wires up in a later phase).
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-2xs uppercase tracking-wide text-muted">
                <tr className="border-b border-border">
                  <th className="px-2 py-2 text-left">Step</th>
                  <th className="px-2 py-2 text-left">Try</th>
                  <th className="px-2 py-2 text-left">Status</th>
                  <th className="px-2 py-2 text-right">Duration</th>
                  <th className="px-2 py-2 text-left">Log</th>
                </tr>
              </thead>
              <tbody>
                {q.data.Steps.map((s) => (
                  <tr
                    key={s.StepID}
                    className="border-b border-border last:border-0"
                  >
                    <td className="px-2 py-2 font-mono text-xs">
                      {s.AirflowTaskID}
                    </td>
                    <td className="px-2 py-2">{s.TryNumber}</td>
                    <td className="px-2 py-2">
                      <Badge tone={statusTone(s.Status as RunStatus)}>
                        {s.Status}
                      </Badge>
                    </td>
                    <td className="px-2 py-2 text-right text-xs">
                      {s.DurationMS
                        ? `${(s.DurationMS / 1000).toFixed(1)}s`
                        : "—"}
                    </td>
                    <td className="px-2 py-2 text-xs">
                      {s.LogURL ? (
                        <a
                          className="underline"
                          href={s.LogURL}
                          target="_blank"
                          rel="noreferrer"
                        >
                          GCS ↗
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Field({
  label,
  value,
  highlight,
}: {
  label: string;
  value: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted">{label}</dt>
      <dd className={`mt-0.5 ${highlight ? "text-yellow-500 font-semibold" : ""}`}>
        {value}
      </dd>
    </div>
  );
}

function statusTone(
  s: string,
): "success" | "danger" | "warning" | "muted" | "accent" {
  switch (s) {
    case "success":
      return "success";
    case "failed":
      return "danger";
    case "partial":
      return "warning";
    case "running":
      return "accent";
    case "cancelled":
      return "muted";
    default:
      return "muted";
  }
}

// ----- watermark + summary helpers -----

interface WmRow {
  key: string;
  before: string | null;
  after: string | null;
  advanced: boolean;
}

function diffWatermark(
  before?: Record<string, unknown>,
  after?: Record<string, unknown>,
): WmRow[] {
  if (!before && !after) return [];
  const keys = new Set<string>([
    ...Object.keys(before ?? {}),
    ...Object.keys(after ?? {}),
  ]);
  const rows: WmRow[] = [];
  for (const k of keys) {
    const b = before?.[k];
    const a = after?.[k];
    rows.push({
      key: k,
      before: b == null ? null : String(b),
      after: a == null ? null : String(a),
      advanced: a != null && a !== b,
    });
  }
  rows.sort((x, y) => x.key.localeCompare(y.key));
  return rows;
}

function entriesOf(obj?: Record<string, unknown>): [string, unknown][] {
  if (!obj || typeof obj !== "object") return [];
  return Object.entries(obj);
}

function renderScalar(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function runningSeconds(startedMS: number): number {
  return Math.max(0, (Date.now() - startedMS) / 1000);
}
